import { useEffect, useMemo, useState } from "react";
import { Box, Spinner } from "@primer/react";
import {
  AlertIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  FileIcon,
  KeyIcon,
  MeterIcon,
  PulseIcon,
  StackIcon,
} from "@primer/octicons-react";
import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../components/DashboardLayout";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../config/firebase";
import { useWallet } from "../hooks/useWallet";

type RangeDays = 7 | 30 | 90;

interface AnalyticsLog {
  id: string;
  apiKeyId: string;
  status: string;
  error?: string;
  templateId?: string;
  createdAt: Date;
}

interface TrendBucket {
  label: string;
  value: number;
  start: Date;
  end: Date;
}

function number(value: number) {
  return new Intl.NumberFormat("en-US").format(value);
}

function buildTrend(logs: AnalyticsLog[], range: RangeDays): TrendBucket[] {
  const bucketCount = range === 7 ? 7 : 15;
  const now = new Date();
  const rangeStart = new Date(now);
  rangeStart.setHours(0, 0, 0, 0);
  rangeStart.setDate(rangeStart.getDate() - range + 1);
  const bucketDuration = (range * 24 * 60 * 60 * 1000) / bucketCount;

  return Array.from({ length: bucketCount }, (_, index) => {
    const start = new Date(rangeStart.getTime() + bucketDuration * index);
    const end = new Date(rangeStart.getTime() + bucketDuration * (index + 1));
    const value = logs.filter((log) => log.createdAt >= start && log.createdAt < end).length;
    const label = range === 7
      ? start.toLocaleDateString("en-US", { weekday: "short" })
      : start.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return { label, value, start, end };
  });
}

export default function Analytics() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { walletData, loading: walletLoading, error: walletError } = useWallet();
  const [range, setRange] = useState<RangeDays>(30);
  const [templateCount, setTemplateCount] = useState(0);
  const [apiKeyCount, setApiKeyCount] = useState(0);
  const [logs, setLogs] = useState<AnalyticsLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadAnalytics = async () => {
      if (!user) return;
      setLoading(true);
      setError(null);

      try {
        const userRef = doc(db, "users", user.uid);
        const [templateSnapshot, apiKeySnapshot] = await Promise.all([
          getDocs(query(collection(db, "templates"), where("user_ref", "==", userRef))),
          getDocs(query(collection(db, "api_keys"), where("user_ref", "==", userRef))),
        ]);

        const rangeStart = new Date();
        rangeStart.setHours(0, 0, 0, 0);
        rangeStart.setDate(rangeStart.getDate() - range + 1);

        const logSnapshots = await Promise.all(
          apiKeySnapshot.docs.map((apiKeyDocument) => {
            const apiKeyRef = doc(db, "api_keys", apiKeyDocument.id);
            return getDocs(
              query(
                collection(db, "api_key_history"),
                where("api_key_ref", "==", apiKeyRef),
                orderBy("created_at", "desc"),
                limit(500),
              ),
            ).then((snapshot) => ({ apiKeyId: apiKeyDocument.id, snapshot }));
          }),
        );

        if (cancelled) return;

        const loadedLogs = logSnapshots
          .flatMap(({ apiKeyId, snapshot }) =>
            snapshot.docs.map((historyDocument) => {
              const data = historyDocument.data();
              return {
                id: historyDocument.id,
                apiKeyId,
                status: data.status || "Unknown",
                error: data.error,
                templateId: data.template_id,
                createdAt: data.created_at?.toDate?.() || new Date(),
              } satisfies AnalyticsLog;
            }),
          )
          .filter((log) => log.createdAt >= rangeStart)
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

        setTemplateCount(templateSnapshot.size);
        setApiKeyCount(apiKeySnapshot.size);
        setLogs(loadedLogs);
      } catch (loadError) {
        console.error("Error loading analytics:", loadError);
        if (!cancelled) setError("We couldn’t load all analytics data. Try refreshing the page.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAnalytics();
    return () => {
      cancelled = true;
    };
  }, [range, user]);

  const trend = useMemo(() => buildTrend(logs, range), [logs, range]);
  const maxTrendValue = Math.max(...trend.map((bucket) => bucket.value), 1);
  const successCount = logs.filter((log) => ["done", "success", "completed"].includes(log.status.toLowerCase())).length;
  const failedCount = logs.filter((log) => ["failed", "error"].includes(log.status.toLowerCase()) || Boolean(log.error)).length;
  const successRate = logs.length ? Math.round((successCount / logs.length) * 100) : null;
  const freeUsed = walletData?.free_usage || 0;
  const freeLimit = walletData?.free_usage_limit || 1000;
  const freeRemaining = Math.max(freeLimit - freeUsed, 0);
  const tokenUsed = walletData?.token_usage || 0;
  const availableTokens = walletData?.available_tokens || 0;
  const totalGenerations = freeUsed + tokenUsed;
  const recentLogs = logs.slice(0, 8);
  const isLoading = loading || walletLoading;

  return (
    <DashboardLayout>
      <Box className="app-page analytics-page">
        <Box className="app-page-header analytics-page-header">
          <Box className="app-page-heading">
            <h1>Analytics</h1>
            <p className="app-page-subtitle">Monitor generations, token consumption, reliability, and account activity.</p>
          </Box>
          <div className="analytics-range" aria-label="Analytics time range">
            {([7, 30, 90] as RangeDays[]).map((days) => (
              <button
                type="button"
                key={days}
                aria-pressed={range === days}
                onClick={() => setRange(days)}
              >
                {days} days
              </button>
            ))}
          </div>
        </Box>

        {(error || walletError) && <div className="analytics-alert"><AlertIcon size={16} />{error || walletError}</div>}

        {isLoading ? (
          <div className="analytics-loading"><Spinner size="large" /><span>Loading analytics…</span></div>
        ) : (
          <>
            <section className="analytics-metrics" aria-label="Account overview">
              <article className="analytics-metric-card">
                <span className="analytics-metric-icon"><StackIcon size={18} /></span>
                <div><small>Total generations</small><strong>{number(totalGenerations)}</strong><span>All time</span></div>
              </article>
              <article className="analytics-metric-card">
                <span className="analytics-metric-icon is-success"><CheckCircleIcon size={18} /></span>
                <div><small>Success rate</small><strong>{successRate === null ? "—" : `${successRate}%`}</strong><span>{number(logs.length)} requests in range</span></div>
              </article>
              <article className="analytics-metric-card">
                <span className="analytics-metric-icon is-accent"><MeterIcon size={18} /></span>
                <div><small>Tokens available</small><strong>{number(availableTokens)}</strong><span>{number(tokenUsed)} paid tokens used</span></div>
              </article>
              <article className="analytics-metric-card">
                <span className="analytics-metric-icon"><PulseIcon size={18} /></span>
                <div><small>Free generations</small><strong>{number(freeRemaining)}</strong><span>{number(freeUsed)} of {number(freeLimit)} used</span></div>
              </article>
            </section>

            <section className="analytics-main-grid">
              <article className="app-panel analytics-chart-card">
                <header className="analytics-card-header">
                  <div><h2>Generation activity</h2><p>API requests during the selected period.</p></div>
                  <span>{number(logs.length)} total</span>
                </header>
                <div className="analytics-chart" aria-label={`Generation activity over ${range} days`}>
                  <div className="analytics-chart-scale"><span>{maxTrendValue}</span><span>{maxTrendValue > 1 ? Math.round(maxTrendValue / 2) : ""}</span><span>0</span></div>
                  <div className="analytics-bars" style={{ gridTemplateColumns: `repeat(${trend.length}, minmax(7px, 1fr))` }}>
                    {trend.map((bucket, index) => (
                      <div className="analytics-bar-column" key={bucket.start.toISOString()} title={`${bucket.label}: ${bucket.value} requests`}>
                        <div className="analytics-bar-track">
                          <div className={`analytics-bar${bucket.value ? "" : " is-empty"}`} style={{ height: `${bucket.value ? Math.max((bucket.value / maxTrendValue) * 100, 8) : 1}%` }} />
                        </div>
                        <span>{index === 0 || index === trend.length - 1 || index % Math.ceil(trend.length / 5) === 0 ? bucket.label : ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {!logs.length && <p className="analytics-chart-empty">Activity will appear here after your first API request.</p>}
              </article>

              <article className="app-panel analytics-token-card">
                <header className="analytics-card-header"><div><h2>Token allocation</h2><p>Free and paid usage at a glance.</p></div></header>
                <div className="analytics-token-stat"><span>Free allowance</span><strong>{number(freeRemaining)} remaining</strong></div>
                <div className="analytics-progress"><span style={{ width: `${Math.min((freeUsed / Math.max(freeLimit, 1)) * 100, 100)}%` }} /></div>
                <div className="analytics-progress-labels"><span>{number(freeUsed)} used</span><span>{number(freeLimit)} total</span></div>
                <div className="analytics-token-divider" />
                <div className="analytics-token-stat"><span>Purchased tokens</span><strong>{number(availableTokens)} available</strong></div>
                <div className="analytics-progress is-accent"><span style={{ width: `${Math.min((tokenUsed / Math.max(tokenUsed + availableTokens, 1)) * 100, 100)}%` }} /></div>
                <div className="analytics-progress-labels"><span>{number(tokenUsed)} consumed</span><span>{number(tokenUsed + availableTokens)} purchased</span></div>
                <button type="button" className="analytics-card-link" onClick={() => navigate("/dashboard/usage")}>View billing details <ArrowRightIcon size={14} /></button>
              </article>
            </section>

            <section className="analytics-secondary-grid">
              <article className="app-panel analytics-resources-card">
                <header className="analytics-card-header"><div><h2>Workspace resources</h2><p>Current account footprint.</p></div></header>
                <button type="button" onClick={() => navigate("/dashboard/templates")}><span className="analytics-resource-icon"><FileIcon size={17} /></span><span><small>Templates</small><strong>{number(templateCount)}</strong></span><ArrowRightIcon size={14} /></button>
                <button type="button" onClick={() => navigate("/dashboard/api-keys")}><span className="analytics-resource-icon"><KeyIcon size={17} /></span><span><small>API keys</small><strong>{number(apiKeyCount)}</strong></span><ArrowRightIcon size={14} /></button>
                <button type="button" onClick={() => navigate("/dashboard/usage")}><span className="analytics-resource-icon"><MeterIcon size={17} /></span><span><small>Wallet balance</small><strong>${(walletData?.amount || 0).toFixed(2)}</strong></span><ArrowRightIcon size={14} /></button>
                <div className="analytics-resource-row"><span className="analytics-resource-icon is-danger"><AlertIcon size={17} /></span><span><small>Failed requests</small><strong>{number(failedCount)}</strong></span><span className="analytics-resource-period">{range}d</span></div>
              </article>

              <article className="app-panel analytics-activity-card">
                <header className="analytics-card-header"><div><h2>Recent activity</h2><p>Latest generation requests across all API keys.</p></div></header>
                {recentLogs.length ? (
                  <div className="analytics-activity-list">
                    {recentLogs.map((log) => {
                      const successful = ["done", "success", "completed"].includes(log.status.toLowerCase()) && !log.error;
                      return (
                        <div className="analytics-activity-row" key={log.id}>
                          <span className={`analytics-activity-status${successful ? " is-success" : " is-danger"}`}>{successful ? <CheckCircleIcon size={16} /> : <AlertIcon size={16} />}</span>
                          <span className="analytics-activity-copy"><strong>{log.templateId ? `Template ${log.templateId}` : "PDF generation"}</strong><small><ClockIcon size={12} /> {formatDistanceToNow(log.createdAt)} ago</small></span>
                          <span className={`analytics-status-pill${successful ? " is-success" : " is-danger"}`}>{successful ? "Completed" : "Failed"}</span>
                          <button type="button" aria-label="View API key logs" onClick={() => navigate(`/dashboard/api-usage/${log.apiKeyId}`)}><ArrowRightIcon size={14} /></button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="analytics-activity-empty"><PulseIcon size={24} /><strong>No activity yet</strong><span>Requests made with your API keys will appear here.</span></div>
                )}
              </article>
            </section>
          </>
        )}
      </Box>
    </DashboardLayout>
  );
}
